import {
  APPLIANCES,
  BATTERY_EU_PER_UNIT,
  EFFICIENCY_DRAIN_FACTOR,
  LEAK_RATE_PER_HOUR,
  SCENARIOS,
  SOLAR_PEAK_EU_PER_HOUR,
} from '../data';

import {
  ASLEEP_HOURS,
  BATTERY_RESTART_EU,
  COMFORT_BAND_F,
  DARK_HOURS,
  EXTREME_BAND_F,
  FOOD_SPOILS_AT_MINUTES,
  FRIDGE_GRACE_MINUTES,
  GAME_START_HOUR_OF_DAY,
  GRID_CHARGE_EU_PER_HOUR,
  HVAC_DEADBAND_F,
  HVAC_DEGREES_PER_HOUR,
  HVAC_TARGET_F,
  INDOOR_START_F,
  MOOD_DRIFT_BLACKOUT,
  MOOD_DRIFT_EXTREME,
  MOOD_DRIFT_FOOD_RISK,
  MOOD_DRIFT_NO_WIFI,
  MOOD_DRIFT_UNCOMFORTABLE,
  MOOD_MAX,
  MOOD_START,
  OUTAGE_START_MINUTE,
  RESTORE_MINUTE,
  SPOILAGE_MOOD_HIT,
  WIFI_GRUMBLE_AT_MINUTES,
} from './constants';
import {
  ActiveLoad,
  ApplianceId,
  GameAction,
  GameEvent,
  GameState,
  PrepConfig,
  Scenario,
  ScenarioId,
  ShopItemId,
} from './types';

/** minutes from game start (10:00 AM) until midnight, when "day two" begins */
const DAY_TWO_MINUTE = (24 - GAME_START_HOUR_OF_DAY) * 60;
/** endless mode ends in glory after a full extra day off-grid */
const ENDLESS_CAP_MINUTES = 1440;

const DEFAULT_PREP: PrepConfig = {
  difficultyId: 'tight',
  purchases: {},
  scenarioId: ScenarioId.LOVELY_SPRING_DAY,
};

export const batteryCapacityEu = (prep: PrepConfig): number =>
  (prep.purchases[ShopItemId.BATTERY] ?? 0) * BATTERY_EU_PER_UNIT;

const solarPeakEuPerHour = (prep: PrepConfig): number => {
  if (prep.purchases[ShopItemId.SOLAR_LARGE]) return SOLAR_PEAK_EU_PER_HOUR.large;
  if (prep.purchases[ShopItemId.SOLAR_SMALL]) return SOLAR_PEAK_EU_PER_HOUR.small;
  return SOLAR_PEAK_EU_PER_HOUR.none;
};

export const hourOfDay = (gameMinutes: number): number => ((GAME_START_HOUR_OF_DAY * 60 + gameMinutes) / 60) % 24;

export const clockLabel = (gameMinutes: number): string => {
  const hod = Math.floor(hourOfDay(gameMinutes));
  const minute = Math.floor(gameMinutes % 60);
  const meridiem = hod < 12 ? 'AM' : 'PM';
  const hour12 = hod % 12 === 0 ? 12 : hod % 12;
  return `${hour12}:${String(minute).padStart(2, '0')} ${meridiem}`;
};

export const isDarkOut = (gameMinutes: number): boolean => {
  const hod = hourOfDay(gameMinutes);
  return hod >= DARK_HOURS.from || hod < DARK_HOURS.until;
};

export const isHouseholdAsleep = (gameMinutes: number): boolean => {
  const hod = hourOfDay(gameMinutes);
  return hod >= ASLEEP_HOURS.from || hod < ASLEEP_HOURS.until;
};

export const areKidsHome = (state: GameState): boolean =>
  state.gameMinutes >= SCENARIOS[state.prep.scenarioId].kidsHomeFromMinute;

/** 0–1 sun height curve: nothing before 7 AM or after 7 PM, peak at 1 PM */
const solarBell = (hod: number): number => (hod <= 7 || hod >= 19 ? 0 : Math.sin(((hod - 7) / 12) * Math.PI));

/** scenario weather multiplier on solar at the current game time (1 = clear sky) */
export const weatherSolarFactor = (state: GameState): number => {
  const scenario = SCENARIOS[state.prep.scenarioId];
  const factors = state.gameMinutes < DAY_TWO_MINUTE ? scenario.solarFactorByHour : scenario.solarFactorByHourDayTwo;
  return factors[Math.floor(hourOfDay(state.gameMinutes))];
};

export const solarGenEuPerHour = (state: GameState): number =>
  solarPeakEuPerHour(state.prep) * solarBell(hourOfDay(state.gameMinutes)) * weatherSolarFactor(state);

/** Smart thermostat: HVAC only runs (and draws) once the house drifts off target. */
export const isHvacWorking = (state: GameState): boolean =>
  !state.dark &&
  state.activeLoads.some(({ applianceId }) => applianceId === ApplianceId.HVAC) &&
  Math.abs(HVAC_TARGET_F - state.indoorTempF) >= HVAC_DEADBAND_F;

/** the loads every run starts with, restored automatically when a dark house relights */
const essentialLoads = (): ActiveLoad[] =>
  Object.values(APPLIANCES)
    .filter(({ essential }) => essential)
    .map(({ id }) => ({ applianceId: id }));

export const isLoadRunning = (state: GameState, applianceId: ApplianceId): boolean =>
  !state.dark && state.activeLoads.some((load) => load.applianceId === applianceId);

export const drainEuPerHour = (state: GameState): number => {
  if (state.dark) return 0;

  const efficiency = state.prep.purchases[ShopItemId.EFFICIENCY_PACK] ? EFFICIENCY_DRAIN_FACTOR : 1;
  const lights = isDarkOut(state.gameMinutes) ? APPLIANCES[ApplianceId.LIGHTS].drainPerHour : 0;
  const active = state.activeLoads.reduce((sum, { applianceId }) => {
    if (applianceId === ApplianceId.HVAC && !isHvacWorking(state)) return sum;
    return sum + APPLIANCES[applianceId].drainPerHour;
  }, 0);

  return (lights + active) * efficiency;
};

export const createInitialState = (prep: PrepConfig = DEFAULT_PREP): GameState => ({
  activeLoads: [],
  batteryEu: 0,
  blackoutMinutes: 0,
  chatLog: [],
  comfortMinutes: 0,
  dark: false,
  decisions: [],
  endless: false,
  firedEventIds: [],
  fridgeOffMinutes: 0,
  gameMinutes: 0,
  gridUp: true,
  indoorTempF: INDOOR_START_F,
  mood: MOOD_START,
  moodSamples: 0,
  moodSum: 0,
  overtimeMinutes: 0,
  pendingEvent: undefined,
  phase: 'prep',
  prep,
  speed: 1,
  wifiOffMinutes: 0,
});

const findDueEvent = (scenario: Scenario, gameMinutes: number, firedEventIds: string[]) =>
  scenario.events.find(({ atMinute, id }) => atMinute <= gameMinutes && !firedEventIds.includes(id));

/**
 * A due event whose condition isn't met is skipped silently — its moment has passed.
 * Boundary rule for paired Above/Below variants sharing a threshold: Above is strict (>),
 * Below is inclusive (<=), so a house sitting exactly on the threshold fires the Below
 * variant — never neither.
 */
export const eventConditionMet = (event: GameEvent, state: GameState): boolean => {
  const ownsSolar = solarPeakEuPerHour(state.prep) > 0;
  if (event.requiresSolar !== undefined && event.requiresSolar !== ownsSolar) return false;
  if (event.requiresIndoorAbove !== undefined && state.indoorTempF <= event.requiresIndoorAbove) return false;
  if (event.requiresIndoorBelow !== undefined && state.indoorTempF > event.requiresIndoorBelow) return false;
  if (event.requiresKidsHome && !areKidsHome(state)) return false;
  if (event.requiresLoadOff !== undefined && isLoadRunning(state, event.requiresLoadOff)) return false;
  if (event.requiresLoadOn !== undefined && !isLoadRunning(state, event.requiresLoadOn)) return false;
  return true;
};

/** Posts due announcements to the chat and pauses on the first event that needs an answer. */
const fireDueEvents = (state: GameState, scenario: Scenario): GameState => {
  let next = state;
  let due = findDueEvent(scenario, next.gameMinutes, next.firedEventIds);
  while (due && !next.pendingEvent) {
    const fires = eventConditionMet(due, next);
    next = {
      ...next,
      chatLog: fires ? [...next.chatLog, { atMinute: next.gameMinutes, from: due.from, text: due.text }] : next.chatLog,
      firedEventIds: [...next.firedEventIds, due.id],
      pendingEvent: fires && due.responses.length > 0 ? due : undefined,
    };
    due = findDueEvent(scenario, next.gameMinutes, next.firedEventIds);
  }
  return next;
};

const driftMood = (state: GameState): number => {
  const { indoorTempF: temp } = state;
  const extreme = temp < EXTREME_BAND_F.min || temp > EXTREME_BAND_F.max;
  const uncomfortable = temp < COMFORT_BAND_F.min || temp > COMFORT_BAND_F.max;

  let driftPerHour = 0;
  if (extreme) driftPerHour += MOOD_DRIFT_EXTREME;
  else if (uncomfortable) driftPerHour += MOOD_DRIFT_UNCOMFORTABLE;
  if (state.dark) driftPerHour += MOOD_DRIFT_BLACKOUT;
  if (!isLoadRunning(state, ApplianceId.FRIDGE) && state.fridgeOffMinutes > FRIDGE_GRACE_MINUTES) {
    driftPerHour += MOOD_DRIFT_FOOD_RISK;
  }
  // a dead Wi-Fi only hurts while someone's awake to miss it
  if (!isLoadRunning(state, ApplianceId.WIFI) && !isHouseholdAsleep(state.gameMinutes)) {
    driftPerHour += MOOD_DRIFT_NO_WIFI;
  }

  return Math.min(MOOD_MAX, Math.max(0, state.mood - driftPerHour / 60));
};

/** One-time consequence messages for unpowered essentials (cumulative — food doesn't un-spoil). */
const applyEssentialFallout = (state: GameState): GameState => {
  let next: GameState = {
    ...state,
    fridgeOffMinutes: state.fridgeOffMinutes + (isLoadRunning(state, ApplianceId.FRIDGE) ? 0 : 1),
    // asleep hours don't count toward the Wi-Fi fuse — an overnight cut nobody noticed never happened
    wifiOffMinutes:
      state.wifiOffMinutes + (isLoadRunning(state, ApplianceId.WIFI) || isHouseholdAsleep(state.gameMinutes) ? 0 : 1),
  };

  if (next.fridgeOffMinutes >= FOOD_SPOILS_AT_MINUTES && !next.firedEventIds.includes('sys-food-spoiled')) {
    next = {
      ...next,
      chatLog: [
        ...next.chatLog,
        {
          atMinute: next.gameMinutes,
          from: 'Home',
          text: 'The fridge has been off too long — everything inside is a science experiment now.',
        },
      ],
      firedEventIds: [...next.firedEventIds, 'sys-food-spoiled'],
      mood: Math.max(0, next.mood - SPOILAGE_MOOD_HIT),
    };
  }
  // Riley only notices the dead Wi-Fi while home and awake — the grumble waits otherwise
  if (
    next.wifiOffMinutes >= WIFI_GRUMBLE_AT_MINUTES &&
    !next.firedEventIds.includes('sys-wifi-grumble') &&
    areKidsHome(next) &&
    !isHouseholdAsleep(next.gameMinutes)
  ) {
    next = {
      ...next,
      chatLog: [
        ...next.chatLog,
        { atMinute: next.gameMinutes, from: 'Riley', text: 'is the wifi DEAD?? my streaks!! 😤' },
      ],
      firedEventIds: [...next.firedEventIds, 'sys-wifi-grumble'],
    };
  }
  return next;
};

const applyMinute = (state: GameState): GameState => {
  const scenario = SCENARIOS[state.prep.scenarioId];
  const gameMinutes = state.gameMinutes + 1;
  const gridUp = gameMinutes < OUTAGE_START_MINUTE;
  const capacity = batteryCapacityEu(state.prep);

  let next: GameState = {
    ...state,
    activeLoads: state.activeLoads.filter(({ untilMinute }) => untilMinute === undefined || untilMinute > gameMinutes),
    gameMinutes,
    gridUp,
  };

  // energy
  const solar = solarGenEuPerHour(next);
  const drain = drainEuPerHour(next);
  let { batteryEu, dark } = next;
  if (gridUp) {
    batteryEu = Math.min(capacity, batteryEu + (GRID_CHARGE_EU_PER_HOUR + solar) / 60);
  } else {
    batteryEu = Math.min(capacity, batteryEu + (solar - drain) / 60);
    if (batteryEu <= 0) {
      batteryEu = 0;
      if (!dark) {
        dark = true;
        next = {
          ...next,
          activeLoads: [],
          chatLog: [
            ...next.chatLog,
            { atMinute: gameMinutes, from: 'Home', text: 'The battery is empty. The whole house goes dark.' },
          ],
        };
      }
    } else if (dark && batteryEu >= BATTERY_RESTART_EU) {
      dark = false;
      next = {
        ...next,
        activeLoads: essentialLoads(),
        chatLog: [
          ...next.chatLog,
          {
            atMinute: gameMinutes,
            from: 'Home',
            text: 'The battery caught enough sun — the essentials hum back to life.',
          },
        ],
      };
    }
  }

  // temperature
  const outdoor = scenario.outdoorTempByHour[Math.floor(hourOfDay(gameMinutes))];
  const leak = state.prep.purchases[ShopItemId.INSULATION] ? LEAK_RATE_PER_HOUR.insulated : LEAK_RATE_PER_HOUR.standard;
  let indoorTempF = next.indoorTempF + ((outdoor - next.indoorTempF) * leak) / 60;
  const hvacRunning = isHvacWorking({ ...next, dark });
  if (hvacRunning) {
    const step = HVAC_DEGREES_PER_HOUR / 60;
    const delta = HVAC_TARGET_F - indoorTempF;
    indoorTempF += Math.abs(delta) < step ? delta : Math.sign(delta) * step;
  }

  next = { ...next, batteryEu, dark, indoorTempF };

  // mood + tallies
  const mood = driftMood(next);
  const inComfortBand = indoorTempF >= COMFORT_BAND_F.min && indoorTempF <= COMFORT_BAND_F.max;
  next = {
    ...next,
    blackoutMinutes: next.blackoutMinutes + (dark ? 1 : 0),
    comfortMinutes: next.comfortMinutes + (!gridUp && inComfortBand ? 1 : 0),
    mood,
    moodSamples: next.moodSamples + 1,
    moodSum: next.moodSum + mood,
    overtimeMinutes: next.overtimeMinutes + (next.endless && gameMinutes > RESTORE_MINUTE ? 1 : 0),
  };

  next = applyEssentialFallout(next);
  next = fireDueEvents(next, scenario);

  // end of the line: scheduled restore, or the endless run finally going dark
  if (!next.endless && gameMinutes >= RESTORE_MINUTE) {
    return {
      ...next,
      chatLog: [...next.chatLog, { atMinute: gameMinutes, from: 'Grid Alert', text: 'Power restored! You made it.' }],
      pendingEvent: undefined,
      phase: 'scorecard',
    };
  }
  if (next.endless && (next.dark || next.overtimeMinutes >= ENDLESS_CAP_MINUTES)) {
    return { ...next, pendingEvent: undefined, phase: 'scorecard' };
  }
  return next;
};

export const gameReducer = (state: GameState, action: GameAction): GameState => {
  switch (action.type) {
    case 'start': {
      const started: GameState = {
        ...createInitialState(action.prep),
        activeLoads: essentialLoads(),
        batteryEu: batteryCapacityEu(action.prep),
        phase: 'playing',
      };
      return fireDueEvents(started, SCENARIOS[action.prep.scenarioId]);
    }

    case 'tick': {
      let next = state;
      Array.from({ length: Math.max(0, Math.floor(action.deltaMinutes)) }).forEach(() => {
        if (next.phase === 'playing' && !next.pendingEvent) next = applyMinute(next);
      });
      return next;
    }

    case 'respond': {
      const event = state.pendingEvent;
      if (!event) return state;
      const responseIndex = event.responses[action.responseIndex] ? action.responseIndex : 0;
      const response = event.responses[responseIndex];
      const { durationMinutes, moodDelta = 0, reaction, startLoads = [], stopLoads = [] } = response.effect;

      const stopped = state.activeLoads.filter(
        ({ applianceId }) => !stopLoads.includes(applianceId) && !startLoads.includes(applianceId)
      );
      // a dark house can't start anything — the choice lands, the power doesn't
      const started = state.dark
        ? []
        : startLoads.map((applianceId) => ({
            applianceId,
            untilMinute: durationMinutes === undefined ? undefined : state.gameMinutes + durationMinutes,
          }));

      const chatLog = [
        ...state.chatLog,
        { atMinute: state.gameMinutes, from: 'You', text: response.label },
        ...(reaction ? [{ atMinute: state.gameMinutes, from: '', text: reaction }] : []),
      ];

      return {
        ...state,
        activeLoads: [...stopped, ...started],
        chatLog,
        decisions: [...state.decisions, { atMinute: state.gameMinutes, eventId: event.id, responseIndex }],
        mood: Math.min(MOOD_MAX, Math.max(0, state.mood + moodDelta)),
        pendingEvent: undefined,
      };
    }

    case 'toggleLoad': {
      if (state.phase !== 'playing' || state.dark) return state;
      const isOn = state.activeLoads.some(({ applianceId }) => applianceId === action.applianceId);
      const activeLoads = isOn
        ? state.activeLoads.filter(({ applianceId }) => applianceId !== action.applianceId)
        : [...state.activeLoads, { applianceId: action.applianceId }];
      return { ...state, activeLoads };
    }

    case 'setSpeed':
      return { ...state, speed: action.speed };

    case 'startEndless':
      return { ...state, endless: true, phase: 'playing' };

    case 'restart':
      return createInitialState(state.prep);

    default:
      return state;
  }
};
